import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "@/App";
import { AuthProvider } from "@/hooks/useAuth";
// Initialize i18next (side-effect import — `./i18n` calls `.init()` at
// module load) and bind the singleton into the shared dashboard-render
// package so `useTranslation()` inside the package returns the same
// instance the builder owns.
import i18n from "@/i18n";
import { setI18nInstance } from "@pervasivemind/dashboard-render";
import {
  redirectForUnprefixedPath,
  slugFromPath,
} from "@/lib/tenant-path";
import { getToken } from "@/lib/api";
import { installBfcacheSessionGuard } from "@/lib/bfcache-session-guard";
import "./index.css";

setI18nInstance(i18n);

// QA-UI2-01: Back after logout must not restore the previous session's page.
installBfcacheSessionGuard(getToken, () => window.location.reload());

/** The one screen that lives outside every customer prefix. */
const LOGIN_PATH = "/login";

/**
 * Which customer this tab is in, resolved BEFORE the router mounts
 * (Plan E, E4).
 *
 * Every API path and the router's own `basename` derive from the
 * prefix, so a tab that has not settled which customer it is looking at
 * cannot render one correct URL. When the URL does not say, the answer
 * is never invented: one stored session means the destination is
 * unambiguous and the user is moved there; none or several means it is
 * not, and they go to the login page.
 */
function resolveTenant(): string | null {
  const slug = slugFromPath();
  if (slug !== null) return slug;

  const pathname = window.location.pathname;
  if (pathname === LOGIN_PATH) {
    const home = redirectForUnprefixedPath("/");
    if (home !== null) window.location.replace(home);
    return null;
  }

  window.location.replace(redirectForUnprefixedPath(pathname) ?? LOGIN_PATH);
  return null;
}

const tenantSlug = resolveTenant();

const container = document.getElementById("root");
if (!container) {
  throw new Error("#root not found in index.html");
}

createRoot(container).render(
  <StrictMode>
    <BrowserRouter
      basename={tenantSlug === null ? undefined : `/${tenantSlug}`}
    >
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
